import { ArrowLeft, ExternalLink, TrendingUp, Target, Users, BookOpen, ClipboardCheck, CheckCircle, Smartphone, MessageCircle, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import '@/styles/ProductPage.css';
import { Helmet } from "react-helmet-async";

const MarketingStrategy = () => {
  return (
    <div className="product-page marketing-page">
      <Helmet>
        <title>Marketing Strategy App - Stratagease | Grow Your Micro Business</title>
        <meta name="description" content="10+ years of research-backed marketing strategy for micro businesses. No marketing experience needed. Join 1,000+ business owners achieving proven results." />
        <meta property="og:title" content="Marketing Strategy App - Stratagease" />
        <meta property="og:description" content="Grow your micro business with confidence. Proven results from 10+ years of research." />
      </Helmet>
      {/* Header */}
      <header className="product-header" data-testid="product-header">
        <nav className="product-nav" data-testid="product-nav">
          <Button 
            onClick={() => window.location.href = '/'} 
            variant="ghost" 
            className="back-btn"
            data-testid="back-button"
          >
            <ArrowLeft size={20} /> Back to Home
          </Button>
        </nav>
      </header>

      {/* Hero */}
      <section className="product-hero marketing-hero" data-testid="hero-section">
        <div className="hero-badge" data-testid="hero-badge">FEATURED</div>
        <h1 className="product-hero-title" data-testid="hero-title">Marketing Strategy App</h1>
        <p className="product-hero-tagline" data-testid="hero-tagline">
          Grow your micro business with confidence
        </p>
        <div className="research-badge" data-testid="research-badge">
          ✓ 10+ Years of Research & Testing  |  ✓ Doctoral Research-Backed  |  ✓ Proven with 1000+ Businesses
        </div>
        <div className="hero-stats" data-testid="hero-stats">
          <div className="stat" data-testid="stat-revenue">
            <TrendingUp size={32} />
            <span className="stat-value">+13%</span>
            <span className="stat-label">Revenue Growth</span>
          </div>
          <div className="stat" data-testid="stat-users">
            <Users size={32} />
            <span className="stat-value">1000+</span>
            <span className="stat-label">Active Users</span>
          </div>
          <div className="stat" data-testid="stat-success">
            <Target size={32} />
            <span className="stat-value">Proven</span>
            <span className="stat-label">Results</span>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section" data-testid="problem-section">
        <div className="container">
          <h2 className="section-title">The Marketing Challenge for Small Businesses</h2>
          <div className="problem-content">
            <div className="problem-stat">
              <span className="big-stat">50%</span>
              <span className="stat-caption">of small businesses fail without a marketing strategy</span>
            </div>
            <div className="problem-description">
              <p><strong>Tired, frustrated, and confused.</strong> That's how most small business owners feel about marketing.</p>
              <p>You're caught in a catch-22:</p>
              <ul>
                <li>Too small for professional marketing services</li>
                <li>Need to be a marketing expert for self-serve tools</li>
                <li>Stuck copying competitors without a real strategy</li>
              </ul>
              <p className="solution-lead">We created Marketing Strategy to solve this exact problem.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="product-features" data-testid="features-section">
        <div className="container">
          <h2 className="features-title" data-testid="features-title">Marketing Made Easy. Finally.</h2>
          <p className="features-subtitle">Life is short. Spend more time doing what you love, less time figuring out marketing.</p>
          <div className="features-grid">
            <div className="feature-item" data-testid="feature-no-experience">
              <div className="feature-icon green-icon">
                <BookOpen size={48} />
              </div>
              <h3>No Experience Needed</h3>
              <p>Answer simple questions and get your personalized marketing plan. No marketing knowledge required.</p>
            </div>

            <div className="feature-item" data-testid="feature-personalized">
              <div className="feature-icon green-icon">
                <ClipboardCheck size={48} />
              </div>
              <h3>Personalized Plans</h3>
              <p>Every business is unique. Get a strategy tailored specifically to your goals and resources.</p>
            </div>

            <div className="feature-item" data-testid="feature-tested">
              <div className="feature-icon green-icon">
                <CheckCircle size={48} />
              </div>
              <h3>Tested & Proven</h3>
              <p>Backed by years of scientific research and proven to work with real businesses like yours.</p>
            </div>

            <div className="feature-item" data-testid="feature-mobile">
              <div className="feature-icon green-icon">
                <Smartphone size={48} />
              </div>
              <h3>Mobile First</h3>
              <p>Access your strategy anywhere, anytime. No laptop needed to create, use, or manage your plan.</p>
            </div>

            <div className="feature-item" data-testid="feature-plain-language">
              <div className="feature-icon green-icon">
                <MessageCircle size={48} />
              </div>
              <h3>Plain Language</h3>
              <p>No jargon. Easy-to-follow instructions so you can focus on what you love about your business.</p>
            </div>

            <div className="feature-item" data-testid="feature-implementation">
              <div className="feature-icon green-icon">
                <Zap size={48} />
              </div>
              <h3>Implementation Help</h3>
              <p>Do it yourself, use recommended tools, or get Stratagease to implement it for you.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Credibility Section */}
      <section className="founder-section" data-testid="founder-section">
        <div className="container">
          <div className="founder-card">
            <div className="founder-content">
              <h3>Founded by Dr. Raewyn Sleeman</h3>
              <p>Research into small business marketing challenges began in 2011. After working with thousands of business owners and conducting doctoral research, Dr. Sleeman created a framework that uniquely addresses the gaps in small business marketing knowledge.</p>
              <p><strong>The result?</strong> A marketing strategy tool that gives you growth certainty, confidence, and control.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Proven Results Section */}
      <section className="results-section" data-testid="results-section">
        <div className="container">
          <h2 className="section-title">Proven Results from Real Businesses</h2>
          <p className="section-subtitle">Over 10 years of testing with real small businesses across the UK and Canada</p>
          
          <div className="results-grid">
            <div className="result-card">
              <div className="result-number">+13%</div>
              <div className="result-label">Revenue Increase</div>
              <div className="result-business">Kika's Holiday Cottages · UK</div>
            </div>
            
            <div className="result-card">
              <div className="result-number">+19%</div>
              <div className="result-label">Customer Increase</div>
              <div className="result-business">Nicholas Frye Piano Tuning · UK</div>
            </div>
            
            <div className="result-card">
              <div className="result-number">1,233%</div>
              <div className="result-label">Website Visitors</div>
              <div className="result-business">Spotless Cleanrz · Canada</div>
            </div>
            
            <div className="result-card">
              <div className="result-number">+244%</div>
              <div className="result-label">Unpaid Leads</div>
              <div className="result-business">Studio 64 · Canada</div>
            </div>
            
            <div className="result-card">
              <div className="result-number">25,600</div>
              <div className="result-label">Unpaid Post Views</div>
              <div className="result-business">Small Business Bookkeepers · Canada</div>
            </div>
            
            <div className="result-card">
              <div className="result-number">9+ Years</div>
              <div className="result-label">Sustained Growth</div>
              <div className="result-business">Market Hill Animal Hospital · Canada</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section" data-testid="how-it-works-section">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Answer Simple Questions</h3>
              <p>Tell us about your business goals and resources. No marketing jargon required.</p>
            </div>
            
            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Get Your Personalized Strategy</h3>
              <p>Receive a tailored marketing plan built on 10+ years of research and proven frameworks.</p>
            </div>
            
            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Implement & Grow</h3>
              <p>Follow plain-language instructions. DIY, use recommended tools, or let us implement for you.</p>
            </div>
            
            <div className="step-card">
              <div className="step-number">4</div>
              <h3>Track Your Success</h3>
              <p>Monitor your growth with confidence, knowing you have a solid foundation that lasts.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="testimonial-section" data-testid="testimonial-section">
        <div className="container">
          <div className="testimonials-grid">
            <div className="testimonial marketing-testimonial">
              <p className="testimonial-text" data-testid="testimonial-text-1">
                "Really amazing suggestions to promote and push my business forward."
              </p>
              <p className="testimonial-author" data-testid="testimonial-author-1">-Carmen</p>
              <p className="testimonial-business">Small Business Owner · Canada</p>
            </div>
            
            <div className="testimonial marketing-testimonial">
              <p className="testimonial-text" data-testid="testimonial-text-2">
                "It's making such a difference to my business."
              </p>
              <p className="testimonial-author" data-testid="testimonial-author-2">- Nicholas</p>
              <p className="testimonial-business">Nicholas Frye Piano Tuning · UK</p>
            </div>
            
            <div className="testimonial marketing-testimonial">
              <p className="testimonial-text" data-testid="testimonial-text-3">
                "Trusting professionals frees me to work on what I'm better at."
              </p>
              <p className="testimonial-author" data-testid="testimonial-author-3">- Kat</p>
              <p className="testimonial-business">Small Business Owner · Canada</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section marketing-cta-section" data-testid="cta-section">
        <h2 className="cta-title" data-testid="cta-title">From Confusion to Confidence. Start Today.</h2>
        <p className="cta-description" data-testid="cta-description">
          Join 1,000+ micro business owners who've transformed their marketing with proven strategies. Available worldwide.
        </p>
        <p className="cta-guarantee">✓ 10+ Years Research-Backed  ✓ Proven Results  ✓ No Marketing Experience Needed</p>
        <div className="cta-buttons" data-testid="cta-buttons">
          <a 
            href="https://play.google.com/store/apps/details?id=com.app.stratagease" 
            target="_blank" 
            rel="noopener noreferrer"
            className="cta-button marketing-cta-btn"
            data-testid="cta-google-play"
          >
            <ExternalLink size={20} /> Download on Google Play
          </a>
          <a 
            href="https://apps.apple.com/us/app/marketing-strategy/id1634555279" 
            target="_blank" 
            rel="noopener noreferrer"
            className="cta-button marketing-cta-btn"
            data-testid="cta-app-store"
          >
            <ExternalLink size={20} /> Download on App Store
          </a>
        </div>
      </section>
    </div>
  );
};

export default MarketingStrategy;